import React from 'react';
import { Sun, Shield, RefreshCw, Monitor, CheckCircle, Zap, Layers, Radio, Satellite } from 'lucide-react';
import { resilienceFeatures, technicalSpecs } from '../data/mock';

const iconComponents = {
  Sun,
  Shield,
  RefreshCw,
  Monitor,
  Zap,
  Layers,
  Radio,
  Satellite
};

const Resilience = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1509391366360-2e959784a276"
            alt="Solar Infrastructure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-cyan-950/80"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
            <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium">
              Network Resilience
            </span>
            <span className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium flex items-center gap-2">
              <Satellite size={16} />
              {technicalSpecs.starlink.deployments}+ Starlink Deployments
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            <span className="text-cyan-400">Multi-Path Hybrid</span> Resilience
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Built from years of operating in harsh Philippine conditions—Hybrid Power, Fiber-Wireless Overlay, and Hazard-Averse Engineering keep you online when others fail.
          </p>
        </div>
      </section>

      {/* Resilience Features */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {resilienceFeatures.map((feature) => {
              const IconComponent = iconComponents[feature.icon];
              return (
                <div 
                  key={feature.id}
                  className="bg-slate-900 border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300"
                >
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-cyan-400" size={32} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed mb-6">
                        {feature.description}
                      </p>
                      <div className="space-y-2">
                        {feature.benefits.map((benefit, index) => (
                          <div key={index} className="flex items-center text-gray-300">
                            <CheckCircle className="text-cyan-400 mr-2 flex-shrink-0" size={18} />
                            <span className="text-sm">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hybrid Power Architecture */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Hybrid Power <span className="text-cyan-400">Architecture</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Grid + Solar + LiFePO4 battery systems work together seamlessly. Our intelligent power management ensures 24/7 operation regardless of grid stability.
              </p>
              <div className="space-y-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-2">Hybrid System Specifications</h4>
                  <ul className="space-y-2 text-gray-400 text-sm">
                    <li>• High-efficiency monocrystalline solar panels (450W+)</li>
                    <li>• LiFePO4 battery bank (safer, longer lifespan than lead-acid)</li>
                    <li>• MPPT charge controllers for optimal charging</li>
                    <li>• Automatic grid/solar switching (zero-interrupt)</li>
                    <li>• Remote monitoring & battery health alerts</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <h4 className="text-white font-semibold mb-2">Power Independence</h4>
                  <ul className="space-y-2 text-gray-400 text-sm">
                    <li>• 3-5 days of battery backup capacity</li>
                    <li>• Priority: Solar → Battery → Grid (configurable)</li>
                    <li>• Scalable panel arrays for growth</li>
                    <li>• Zero electricity cost in sunny conditions</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <img 
                src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9"
                alt="Solar Farm"
                className="rounded-2xl shadow-2xl"
              />
              <img 
                src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d"
                alt="Solar Panels"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Layer Link Redundancy */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1594915440248-1e419eba6611"
                alt="Network Equipment"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Fiber-Wireless <span className="text-cyan-400">Overlay</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Our Multi-Layer Link Redundancy combines fiber infrastructure with strategic wireless backhauls, ensuring zero single-point failures.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Layers className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Dual-Path Architecture</h4>
                    <p className="text-gray-400 text-sm">
                      Fiber primary with wireless backup ensures connectivity even if one path is damaged
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Radio className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Hazard-Averse Wireless Backhauls</h4>
                    <p className="text-gray-400 text-sm">
                      Long spans bypass hazard zones (flood-prone, landslide-risk areas) where aerial fiber would be vulnerable
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Sub-2-Second Failover</h4>
                    <p className="text-gray-400 text-sm">
                      Automatic path switching in under 2 seconds—users barely notice the transition
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Satellite Connectivity - Starlink */}
      <section className="py-24 bg-gradient-to-br from-slate-950 via-blue-950/20 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-500/10 rounded-xl flex items-center justify-center">
                  <Satellite className="text-blue-400" size={32} />
                </div>
                <div>
                  <span className="px-3 py-1 bg-blue-500/20 border border-blue-500/40 rounded-full text-blue-300 text-xs font-bold">
                    PIONEERING
                  </span>
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Satellite <span className="text-blue-400">Connectivity</span>
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                Starlink Integration for Ultimate Resilience
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                HVGTEC has pioneered Starlink deployments in the region, with <strong className="text-blue-400">{technicalSpecs.starlink.deployments}+ units</strong> successfully installed and integrated into various network architectures. We use Starlink as a high-speed failover backhaul for our Micro POP nodes, ensuring critical connectivity even when primary fiber or terrestrial wireless links are compromised during extreme weather.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">OSPF/SD-WAN Bonding</h4>
                    <p className="text-gray-400 text-sm">
                      Seamlessly bond Starlink with existing ISP backhauls using OSPF or SD-WAN logic for intelligent traffic routing
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Rapid Deployment</h4>
                    <p className="text-gray-400 text-sm">
                      Get remote areas online within hours using Starlink while permanent infrastructure is being built
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="text-blue-400" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Extreme Weather Failover</h4>
                    <p className="text-gray-400 text-sm">
                      When typhoons take down fiber and wireless links, Starlink maintains critical connectivity to the outside world
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-slate-900 border border-blue-500/30 rounded-2xl p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-blue-500/10 rounded-full mb-4">
                    <Satellite className="text-blue-400" size={48} />
                  </div>
                  <h3 className="text-3xl font-bold text-blue-400 mb-2">{technicalSpecs.starlink.deployments}+</h3>
                  <p className="text-gray-400">Starlink Deployments Completed</p>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <p className="text-white font-semibold mb-2">Starlink Business Integration</p>
                    <p className="text-gray-400 text-sm">Enterprise-grade satellite connectivity for businesses requiring high availability</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <p className="text-white font-semibold mb-2">Micro POP Failover</p>
                    <p className="text-gray-400 text-sm">Automatic failover to satellite when primary backhaul is compromised</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <p className="text-white font-semibold mb-2">Maritime & Remote Sites</p>
                    <p className="text-gray-400 text-sm">Extending connectivity to island communities and offshore operations</p>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Redundancy & Monitoring */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Multi-Layer Redundancy
            </h2>
            <p className="text-xl text-gray-400">
              Automatic failover and 24/7 monitoring ensure continuous service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <RefreshCw className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Link Redundancy</h3>
              <p className="text-gray-400 mb-4">
                Multiple uplink paths with automatic failover in under 2 seconds
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Primary & backup ISP links</li>
                <li>• Load balancing</li>
                <li>• Seamless failover</li>
                <li>• Link health monitoring</li>
              </ul>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Power Redundancy</h3>
              <p className="text-gray-400 mb-4">
                Multi-source power with automatic switching and battery backup
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Grid + Solar hybrid</li>
                <li>• Battery backup (3-5 days)</li>
                <li>• Auto power source switching</li>
                <li>• UPS for critical loads</li>
              </ul>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Monitor className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Remote Monitoring</h3>
              <p className="text-gray-400 mb-4">
                24/7 NOC monitoring with instant alerts and diagnostics
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Real-time performance metrics</li>
                <li>• Automatic issue detection</li>
                <li>• Remote troubleshooting</li>
                <li>• Proactive maintenance</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-slate-900 via-cyan-950/20 to-slate-900 border border-cyan-500/30 rounded-2xl p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Typhoon Odette Case Study
              </h2>
              <p className="text-gray-400">
                December 2021 - Category 5 Super Typhoon
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-cyan-400 mb-2">100%</div>
                <div className="text-sm text-gray-400">Of HVGTEC POPs remained operational during the storm</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-cyan-400 mb-2">0</div>
                <div className="text-sm text-gray-400">Hours of downtime for solar-powered Micro POPs</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-cyan-400 mb-2">2-3</div>
                <div className="text-sm text-gray-400">Weeks other ISPs took to restore service in the region</div>
              </div>
            </div>

            <p className="text-gray-300 text-center leading-relaxed">
              While neighboring ISPs experienced weeks of downtime due to grid failures and damaged infrastructure, 
              HVGTEC's solar-powered Micro POPs continued operating throughout and after Typhoon Odette, 
              providing critical connectivity for emergency services and communications.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resilience;
